
import { google } from 'google-maps';
import { Component, Vue, Prop, Inject, Watch } from 'vue-property-decorator';
import GoogleMapPolyLine from '@/components/Map/GoogleMapPolyLine.vue';
import GoogleMapPolygon from '@/components/Map/GoogleMapPolygon.vue';
import GoogleMapLoader from '@/components/Map/GoogleMapLoader.vue';
import { GoogleMapConfig } from '@/flightpath-components/types/GoogleMapConfig';
import { awsConfiguration } from '@/aws-configuration';
import { getModule } from 'vuex-module-decorators';
import MissionStore from '@/store/modules/missions';
import { RenderHelper } from '@/services/RenderHelper';
import { DeployedSinglePathMission } from '@/flightpath-components/models/DeployedSinglePathMission';
import { LayerType } from '@/flightpath-components/models/enums/LayerType';
import GoogleMapGroundOverlay from '@/components/Map/GoogleMapGroundOverlay.vue';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Overlay } from '@/flightpath-components/models/Overlay';
import { LayerList } from '@/flightpath-components/models/LayerList';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import MachineStore from '@/store/modules/machine';
import { GridLocation } from '@/flightpath-components/models/GridLocation';
import GlobalStore from '@/store/modules/global';
import { Guid } from 'guid-typescript';

@Component({
  components: {
    GoogleMapLoader,
    GoogleMapPolyLine,
    GoogleMapPolygon,
    GoogleMapGroundOverlay
  }
})
export default class CompletedMissionMap extends Vue {
  apiKey = awsConfiguration.apiKey;
  polyLines: Array<google.maps.PolylineOptions> = [];
  polygons: Array<google.maps.PolygonOptions> = [];
  overlays: Array<Overlay> = [];
  missionStore = getModule(MissionStore, this.$store);
  machineStore = getModule(MachineStore, this.$store);
  globalStore = getModule(GlobalStore, this.$store);

  @Prop() completedMission!: DeployedSinglePathMission;

  // @Inject()
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  // IoC container provided from App.ts is injected here
  @Inject('container')
  private container!: Container;

  get mapConfig(): GoogleMapConfig {
    return {
      locationControl: false,
      loaderOptions: { libraries: ['geometry'] },
      mapOptions: {
        zoom: 17,
        center: this.globalStore.defaultMapLocation,
        mapTypeId: 'hybrid',
        mapTypeControl: false,
        zoomControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        clickableIcons: false,
        disableDefaultUI: true
      }
    };
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }

  mounted(): void {
    this.renderMission(this.completedMission);
  }

  @Watch('completedMission')
  onCompletedMissionChanged() {
    this.renderMission(this.completedMission);
  }

  async renderMission(mission: DeployedSinglePathMission | null) {
    this.polygons = [];
    this.polyLines = [];
    this.overlays = [];

    if (mission == null) return;

    this.polygons.push(RenderHelper.getFieldPolygonOptionsFromMission(mission));
    this.polygons.push(RenderHelper.getWorkAreaPolygonOptionsFromMission(mission));
    this.polygons.push(RenderHelper.getCropAreaPolygonOptionsFromMission(mission));
    this.polyLines.push(RenderHelper.getPathPolylineOptionsFromMission(mission));

    await this.FetchMapTile(mission);
    if (this.overlays.length == 0) await this.UpdateGraphicsAsync(mission);
    this.UpdateGraphicsAsync(mission);
  }

  private async FetchMapTile(mission: DeployedSinglePathMission) {
    if (this.flightPathService == undefined) return;
    for (const layer of this.getActiveLayers().Layers) {
      const model = await this.flightPathService.getRasterModelMission(mission, layer);
      if (model === undefined) continue;
      if (model.ImageStream != null && model.ImageStream.length > 0) {
        const blob = new Blob([model.ImageStream], { type: 'image/png' });
        const overlay: Overlay = {
          blob: blob,
          sw: model.SouthWest,
          ne: model.NorthEast,
          raster: model,
          zIndex: 1
        };
        const index = this.overlays.findIndex(
          (val) =>
            val.raster.Type == model.Type &&
            val.raster.MachineKey.machineId == model.MachineKey.machineId &&
            val.raster.GridX == model.GridX &&
            val.raster.GridY == model.GridY
        );

        if (index >= 0) {
          if (this.overlays[index].raster.TimeStamp < model.TimeStamp) {
            this.overlays.splice(index, 1, overlay);
          }
        } else {
          this.overlays.push(overlay);
        }
      }
    }
  }

  /**
   * Requests list of grids available for update (update map).
   * Downloads raster image data for that grid layer and converts it into objecturl
   * for processing and rendering via Google Map Ground Overlay
   */
  private async UpdateGraphicsAsync(mission: DeployedSinglePathMission) {
    if (this.flightPathService == undefined) return;

    const jobId = Guid.EMPTY == mission.JobID ? mission.MissionDeploymentID : mission.JobID;

    const response = await this.flightPathService.updateMap(jobId);
    const jsonObject = response.data as Map<string, number>;

    const map: Map<string, number> = new Map<string, number>();
    for (const [key, element] of Object.entries(jsonObject)) {
      map.set(key, element);
    }

    if (map.size > 0) {
      for (const [grid, timestamp] of map) {
        const gridLocation = this.getGridLocation(grid);
        if (gridLocation != undefined) {
          for (const layer of this.getActiveLayers().Layers) {
            const query = this.overlays.filter(
              (o) => timestamp - o.raster.TimeStamp <= 0 && o.raster.Type == layer && o.raster.GridX == gridLocation.X && o.raster.GridY == gridLocation.Y
            );
            if (query.length > 0) {
              continue;
            }
            const model = await this.flightPathService.getRasterModelJobId(gridLocation, layer, mission.TargetMachine.machineId, jobId, timestamp);

            if (model.ImageStream != null && model.ImageStream.length > 0) {
              const blob = new Blob([model.ImageStream], { type: 'image/png' });
              const overlay: Overlay = {
                blob: blob,
                sw: model.SouthWest,
                ne: model.NorthEast,
                raster: model,
                zIndex: 1
              };
              const index = this.overlays.findIndex(
                (val) =>
                  val.raster.Type == model.Type &&
                  val.raster.MachineKey.machineId == model.MachineKey.machineId &&
                  val.raster.GridX == model.GridX &&
                  val.raster.GridY == model.GridY
              );

              if (index >= 0) {
                if (this.overlays[index].raster.TimeStamp < model.TimeStamp) {
                  this.overlays.splice(index, 1, overlay);
                }
              } else {
                this.overlays.push(overlay);
              }
            }
          }
        }
      }
      console.log('Update Map Found', jsonObject);
    }
  }

  getGridLocation(url: string): GridLocation | undefined {
    const regex = /([0-9-])+/g;

    const matches = url.match(regex);
    if (matches != null && matches.length >= 2) {
      const xVal = matches[0];
      const yVal = matches[1];

      return { X: Number.parseInt(xVal), Y: Number.parseInt(yVal) };
    }
  }

  /**
   * List of Active Data Layers to Render
   */
  private getActiveLayers(): LayerList {
    return {
      ErrorNotes: '',
      Layers: [LayerType.EstimatedCoverage, LayerType.DrivenPath]
    };
  }
}
